import { Store, Hash, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { BooleanBadge } from "@/components/table/BooleanBadge";
import { ClienteDTO } from "@/types/cliente.type";
import { useFiliaisByCliente } from "@/hooks/clientes/use-filiais-by-cliente";
import { Button } from "@/components/ui/button";

interface ClienteFiliaisDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
  cliente: ClienteDTO | null;
}

export function ClienteFiliaisDialog({
  open,
  onOpenChange,
  onClose,
  cliente,
}: ClienteFiliaisDialogProps) {
  const { data: filiais, isLoading } = useFiliaisByCliente(cliente?.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-0 overflow-hidden rounded-xl border-border bg-background p-0 shadow-2xl sm:max-w-[550px] sm:rounded-2xl">
        <DialogHeader className="sr-only">
          <DialogTitle>Filiais Vinculadas</DialogTitle>
          <DialogDescription>Lista de filiais associadas a esta matriz.</DialogDescription>
        </DialogHeader>

        {/* HEADER VISUAL */}
        <div className="z-20 flex shrink-0 items-center justify-between border-b bg-card px-5 py-4 sm:px-6">
          <div>
            <h2 className="mb-1 text-base font-bold leading-none tracking-tight text-foreground sm:text-lg">
              Filiais Vinculadas
            </h2>
            <p className="max-w-[300px] truncate text-[10px] text-muted-foreground sm:text-xs">
              Matriz: <span className="font-semibold text-foreground">{cliente?.nome}</span>
            </p>
          </div>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-600/10">
            <Store className="h-5 w-5 text-purple-600" />
          </div>
        </div>

        {/* LISTA DE FILIAIS */}
        <div className="custom-scrollbar max-h-[60vh] overflow-y-auto bg-muted/5 p-5 sm:p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              <span className="text-sm font-medium">Buscando filiais...</span>
            </div>
          ) : !filiais || filiais.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border bg-card py-10 text-center text-sm text-muted-foreground">
              Nenhuma filial encontrada para esta matriz.
            </div>
          ) : (
            <div className="space-y-3">
              {filiais.map((filial) => (
                <div
                  key={filial.id}
                  className="flex items-center justify-between rounded-lg border border-border bg-card p-3.5 shadow-sm transition-colors hover:border-purple-500/30"
                >
                  <div className="flex min-w-0 flex-col gap-1.5 pr-4">
                    <span className="flex items-center gap-2 truncate text-sm font-semibold text-foreground">
                      <Store className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate" title={filial.nome}>
                        {filial.nome}
                      </span>
                    </span>
                    <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-muted-foreground">
                      <span className="flex items-center gap-1 text-primary">
                        <Hash className="h-3 w-3 opacity-70" /> {filial.idPubli}
                      </span>
                      {filial.cnpj && <span>CNPJ: {filial.cnpj}</span>}
                    </div>
                  </div>
                  <div className="shrink-0">
                    <BooleanBadge value={filial.ativo} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t bg-card px-5 py-4 sm:px-6">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
